import React, { createElement, useEffect, useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { FiFileText } from "react-icons/fi";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { ErrorResponse } from '@core/types/errorResponse';
import { useApiError } from '@infrastructure/api/hooks/useApiError';
import { ReportTemplate } from "../types/reportTemplate";
import type { Activity, AttachedActivity } from "@features/admin/activity/types/activity";
import { fetchActivityAsync, fetchAssignedActivityAsync } from "@features/admin/activity/services/activitySvc";
import { useReportTemplateActions } from "@features/report/template/hooks/useReportTemplateActions";
import { removeTemplateActivitiesAsync } from "@features/report/template/services/reportTemplateSvc";
import { fetchAllPages } from "@core/utils/fetchAllPages";
import { SORT_BY_ACTIVITY_NAME, SORT_DIR_ASC } from "@core/constants/listSort";
import { useScopeId } from "@core/hooks/useRouteParamId";
import AssignModalShell from "@core/components/assign/AssignModalShell";
import AssignDualListPanels from "@core/components/assign/AssignDualListPanels";
import AssignAvailableRow from "@core/components/assign/AssignAvailableRow";
import AssignAssignedRow from "@core/components/assign/AssignAssignedRow";
import {
    buildActiveLookup,
    filterActiveForAssignPayload,
    resolveAssignedRowDisplay,
    sortAttachedByOrder,
} from "@core/components/assign/assignDisplay";
import { renumber, setItemOrder } from "@core/components/assign/assignListOrder";

type Props = {
    show: boolean;
    onHide: () => void;
    reportTemplate: ReportTemplate | null;
};

type AttachedActivityRow = AttachedActivity & {
    activity_name?: string;
    activity_description?: string | null;
};

const AssignTemplateActivityActivityModal: React.FC<Props> = ({ show, onHide, reportTemplate }) => {
    const [searchParams] = useSearchParams();
    const resolvedDepartmentId = Number(searchParams.get("did"));
    const resolvedNationId = useScopeId() ?? 0;

    const [allActivities, setAllActivities] = useState<Activity[]>([]);
    const [attached, setAttached] = useState<AttachedActivityRow[]>([]);
    const [initialAttachedIds, setInitialAttachedIds] = useState<number[]>([]);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);

    const showError = useApiError();

    const [qLeft, setQLeft] = useState("");
    const [qRight, setQRight] = useState("");

    const { doTemplateAssignActivity } = useReportTemplateActions();

    const activeActivityIds = useMemo(
        () => new Set(allActivities.map((a) => a.activity_id)),
        [allActivities]
    );
    const activeActivityLookup = useMemo(
        () =>
            buildActiveLookup(
                allActivities,
                (a) => a.activity_id,
                (a) => a.activity_name,
                (a) => a.description ?? a.activity_description ?? undefined
            ),
        [allActivities]
    );

    useEffect(() => {
        if (!show || !reportTemplate?.report_template_id) return;

        let cancelled = false;

        (async () => {
            setLoading(true);

            try {
                const [allItems, assignedItems] = await Promise.all([
                    fetchAllPages<Activity>((page) =>
                        fetchActivityAsync(resolvedNationId, {
                            all: true,
                            page,
                            isActive: true,
                            sortBy: SORT_BY_ACTIVITY_NAME,
                            sortDir: SORT_DIR_ASC,
                        })
                    ),
                    fetchAllPages<any>((page) =>
                        fetchAssignedActivityAsync(resolvedNationId, resolvedDepartmentId, reportTemplate.report_template_id, { all: true, page })
                    ),
                ]);
                if (cancelled) return;

                const seen = new Set<number>();
                const unique = allItems.filter(a => {
                    if (seen.has(a.activity_id)) return false;
                    seen.add(a.activity_id);
                    return true;
                });
                setAllActivities(unique);

                const normalized = renumber(
                    assignedItems.map((a: any, idx: number) => ({
                        activity_id: Number(a.activity_id),
                        list_order: Number(a.list_order ?? a.pivot?.list_order ?? idx + 1),
                        activity_name: a.activity_name,
                        activity_description: a.activity_description ?? a.description ?? null,
                    }))
                );

                setAttached(normalized);
                setInitialAttachedIds(normalized.map(x => x.activity_id));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [show, reportTemplate?.report_template_id, resolvedDepartmentId, resolvedNationId]);

    const attachedIds = useMemo(() => attached.map(x => x.activity_id), [attached]);

    const left = useMemo(() => {
        const base = allActivities.filter(a => !attachedIds.includes(a.activity_id));

        if (!qLeft.trim()) return base;

        const s = qLeft.toLowerCase();

        return base.filter(a => a.activity_name.toLowerCase().includes(s));
    }, [allActivities, attachedIds, qLeft]);

    const visibleAttached = useMemo(() => {
        const ordered = sortAttachedByOrder(attached);

        if (!qRight.trim()) return ordered;

        const s = qRight.toLowerCase();

        return ordered.filter((row) => {
            const display = resolveAssignedRowDisplay(
                row.activity_id,
                activeActivityIds,
                activeActivityLookup,
                {
                    title: row.activity_name ?? `Activity #${row.activity_id}`,
                    sublabel: row.activity_description ?? undefined,
                }
            );
            return (
                display.title.toLowerCase().includes(s) ||
                (display.sublabel?.toLowerCase().includes(s) ?? false)
            );
        });
    }, [attached, qRight, activeActivityIds, activeActivityLookup]);

    const attach = (id: number) => {
        const activity = allActivities.find((a) => a.activity_id === id);
        setAttached(prev => {
            if (prev.some(x => x.activity_id === id)) return prev;
            return renumber([
                ...prev,
                {
                    activity_id: id,
                    list_order: prev.length + 1,
                    activity_name: activity?.activity_name,
                    activity_description: activity?.description ?? activity?.activity_description ?? null,
                },
            ]);
        });
    };

    const detach = (id: number) => {
        setAttached(prev => renumber(prev.filter(x => x.activity_id !== id)));
    };

    const handleOrderChange = (id: number, newOrder: number) => {
        setAttached(prev => setItemOrder(prev, id, newOrder, (x) => x.activity_id));
    };

    const handleClose = () => {
        setQLeft("");
        setQRight("");
        onHide();
    };

    const finishAssignAfterRemove = async () => {
        if (!reportTemplate?.report_template_id) return;

        const activeAttached = filterActiveForAssignPayload(
            attached,
            activeActivityIds,
            (x) => x.activity_id
        );

        if (activeAttached.length > 0) {
            const payload = {
                activities: renumber(activeAttached).map((x) => ({
                    activity_id: x.activity_id,
                    list_order: x.list_order,
                })),
            };

            const response = await doTemplateAssignActivity(
                resolvedNationId,
                resolvedDepartmentId,
                reportTemplate.report_template_id,
                payload
            );
            if (response.isSuccess()) {
                showSuccess(response.getData()?.message);
                onHide();
            } else {
                showError(response.getError() as ErrorResponse);
            }
            return;
        }

        showSuccess("Activities updated successfully.");
        onHide();
    };

    const handleSave = async () => {
        if (!reportTemplate?.report_template_id) return;

        setSaving(true);

        try {
            const currentIds = new Set(attached.map(x => x.activity_id));
            const removedIds = initialAttachedIds.filter(id => !currentIds.has(id));

            if (removedIds.length > 0) {
                const removeResponse = await removeTemplateActivitiesAsync(
                    resolvedNationId,
                    resolvedDepartmentId,
                    reportTemplate.report_template_id,
                    { activity_ids: removedIds }
                );

                if (!removeResponse.isSuccess()) {
                    showError(removeResponse.getError() as ErrorResponse);
                    return;
                }
            }

            await finishAssignAfterRemove();
        } catch (error) {
            showError(error as ErrorResponse);
        } finally {
            setSaving(false);
        }
    };

    const getActivityDescription = (activity: Activity) =>
        activity.description ?? activity.activity_description ?? undefined;

    return (
        <>
        <AssignModalShell
            show={show}
            onHide={handleClose}
            title={
                <>
                    <FiFileText className="me-2" aria-hidden />
                    Manage Activities {reportTemplate ? `: ${reportTemplate.report_template_name}` : ""}
                </>
            }
            saving={saving}
            onSave={() => { void handleSave(); }}
            onCancel={handleClose}
            testId="assign-template-activity-modal"
            cancelTestId="cancel-btn"
            saveTestId="cancel-assign-activities-button"
        >
            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" data-testid="assign-activity-loading" />
                </div>
            ) : (
                <AssignDualListPanels
                    availableTitle="Available Activities"
                    assignedTitle="Assigned to Template"
                    availableCount={left.length}
                    assignedCount={attached.length}
                    availableSearchPlaceholder="Search Activities..."
                    assignedSearchPlaceholder="Search assigned Activities..."
                    availableSearch={qLeft}
                    assignedSearch={qRight}
                    onAvailableSearchChange={setQLeft}
                    onAssignedSearchChange={setQRight}
                    availableSearchTestId="search-available-activites"
                    assignedSearchTestId="search-assigned-activities"
                    availableListTestId="available-activities-list"
                    assignedListTestId="assigned-activities-list"
                    availableEmptyText="No more Activities to add."
                    assignedEmptyText="No Activities assigned to this Template."
                    availableIsEmpty={left.length === 0}
                    assignedIsEmpty={visibleAttached.length === 0}
                    availableItems={left.map((a) =>
                        createElement(AssignAvailableRow, {
                            key: a.activity_id,
                            title: a.activity_name,
                            sublabel: getActivityDescription(a),
                            onAdd: () => attach(a.activity_id),
                            addTestId: `attach-activity-button-${a.activity_id}`,
                        })
                    )}
                    assignedItems={visibleAttached.map((row) => {
                        const display = resolveAssignedRowDisplay(
                            row.activity_id,
                            activeActivityIds,
                            activeActivityLookup,
                            {
                                title: row.activity_name ?? `Activity #${row.activity_id}`,
                                sublabel: row.activity_description ?? undefined,
                            }
                        );

                        return createElement(AssignAssignedRow, {
                            key: row.activity_id,
                            title: display.title,
                            sublabel: display.sublabel,
                            isActive: display.isActive,
                            listOrder: row.list_order,
                            listLength: visibleAttached.length,
                            onOrderChange: (newOrder: number) => handleOrderChange(row.activity_id, newOrder),
                            onRemove: () => detach(row.activity_id),
                            requiredSwitchId: `activity-order-${row.activity_id}`,
                        });
                    })}
                />
            )}
        </AssignModalShell>
        </>
    );
};

export default AssignTemplateActivityActivityModal;
